import { useMemo, useState } from 'react';

import { cn } from '~/utils/cn';
import { formatCentsToBRLCompact } from '~/utils/formatFinance';

import { Text } from '../../atoms/Text/view';
import type { PropsPieChart } from './interfaces';

const SIZE = 184;
const CENTER = SIZE / 2;
const RADIUS = 67;
const STROKE = 30;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export default function PieChart({
  slices,
  height = 224,
  className,
  formatValue = formatCentsToBRLCompact,
}: PropsPieChart) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const total = useMemo(() => slices.reduce((sum, slice) => sum + slice.value, 0), [slices]);
  const visibleIndex = hoveredIndex ?? activeIndex;
  const active = visibleIndex === null ? null : slices[visibleIndex];

  if (!slices.length || total <= 0) {
    return (
      <div className={cn('flex items-center justify-center w-full', className)} style={{ height }}>
        <Text className="text-bodyMD color-intense-06">Sem dados para exibir.</Text>
      </div>
    );
  }

  let accumulated = 0;
  return (
    <div className={cn('w-full flex flex-col items-center', className)}>
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="overflow-visible">
          <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" stroke="#EEF1EF" strokeWidth={STROKE} />
          <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
            {slices.map((slice, index) => {
              const ratio = slice.value / total;
              const dash = Math.max(0, CIRCUMFERENCE * ratio - 3);
              const offset = -CIRCUMFERENCE * accumulated;
              accumulated += ratio;
              const isActive = visibleIndex === index;
              const isDimmed = visibleIndex !== null && !isActive;
              return (
                <circle
                  key={slice.label}
                  cx={CENTER}
                  cy={CENTER}
                  r={RADIUS}
                  fill="none"
                  stroke={slice.color}
                  strokeWidth={isActive ? STROKE + 5 : STROKE}
                  strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                  opacity={isDimmed ? 0.22 : 1}
                  onClick={() => setActiveIndex((previous) => previous === index ? null : index)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{ cursor: 'pointer', transition: 'opacity 180ms ease, stroke-width 180ms ease' }}
                />
              );
            })}
          </g>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-medium pointer-events-none">
          <Text className="text-captionMD color-intense-06 uppercase">
            {active ? active.label : 'Saldo total'}
          </Text>
          <Text className="mt-quark text-headingH2 color-complement-01" numberOfLines={2}>
            {formatValue(active?.value ?? total)}
          </Text>
          {active ? (
            <Text className="mt-nano text-captionLG color-intense-06">
              {((active.value / total) * 100).toFixed(0)}% do total
            </Text>
          ) : null}
        </div>
      </div>

      <div className="w-full mt-smaller grid grid-cols-1 sm:grid-cols-2 gap-x-small gap-y-quark">
        {slices.map((slice, index) => {
          const isActive = visibleIndex === index;
          const isDimmed = visibleIndex !== null && !isActive;
          return (
            <button
              key={slice.label}
              type="button"
              onClick={() => setActiveIndex((previous) => previous === index ? null : index)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onFocus={() => setHoveredIndex(index)}
              onBlur={() => setHoveredIndex(null)}
              className="flex items-center gap-quark text-left min-w-0 rounded-small px-quark py-nano hover:bg-intense-02 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-01"
              style={{ opacity: isDimmed ? 0.42 : 1, transition: 'opacity 180ms ease' }}
              aria-pressed={isActive}
            >
              <span className="w-[10px] h-[10px] rounded-full shrink-0" style={{ backgroundColor: slice.color }} />
              <Text className="text-captionLG color-complement-01 min-w-0 flex-1" numberOfLines={1}>{slice.label}</Text>
              <Text className="text-captionMD color-intense-06 shrink-0">
                {((slice.value / total) * 100).toFixed(0)}%
              </Text>
            </button>
          );
        })}
      </div>
    </div>
  );
}
